import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
} from '@design-system/Sheet';
import { LabProcess } from '@viome/viome-base/src/usecase/Home/Home.model';
import { X } from 'lucide-react';

const parseEmTags = (text: string): React.ReactNode[] => {
  const parts = text.split(/(<em>.*?<\/em>)/g);
  return parts.map((part, i) => {
    const match = part.match(/^<em>(.*?)<\/em>$/);
    return match ? <em key={i}>{match[1]}</em> : part;
  });
};

interface LabProcessSheetProps {
  labProcess: LabProcess;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LabProcessSheet = ({ labProcess, open, onOpenChange }: LabProcessSheetProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetPortal>
      <SheetOverlay />
      <SheetContent side="right" className="flex flex-col gap-0 pr-3 pl-6 bg-background-secondary" variant="glass" hideCloseButton>
        <SheetHeader className="pt-6 mt-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1 pr-4">
              <SheetTitle className="typography-h2 text-primary font-semibold">
                {labProcess.title}
              </SheetTitle>
              <p className="typography-body text-secondary">{labProcess.subtitle}</p>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="text-static-secondary hover:text-static-primary transition-colors mt-1 shrink-0"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <hr className="mt-4 border-gray-200" />
        </SheetHeader>

        <div className="overflow-y-auto flex flex-col gap-0 pb-8 pt-2">
          {labProcess.steps.map((step, i) => (
            <div key={i}>
              <div className="py-5 flex flex-col gap-2">
                <span className="text-xs font-semibold text-teal-500">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="typography-body font-semibold text-primary">{step.title}</p>
                <p className="typography-body text-secondary">
                  {parseEmTags(step.description)}
                </p>
              </div>
              {i < labProcess.steps.length - 1 && <hr className="border-gray-200" />}
            </div>
          ))}
        </div>
      </SheetContent>
    </SheetPortal>
  </Sheet>
);
